// src/pages/InventoryValue.tsx
import { useEffect, useState } from "react";
import api from "../api/axios";
import PageLayout from "../components/layout/PageLayout";
import useAuth from "../auth/useAuth";
import {
  BanknotesIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";

interface BranchInventory {
  branch_id: string;
  branch_name: string;
  total_order_value: string;
  total_sale_value: string;
  currency_name: string;
  currency_symbol: string;
}

const formatNumber = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export default function InventoryValue() {
  const { data: auth } = useAuth();
  const [data, setData] = useState<BranchInventory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.get<BranchInventory[]>(
          "/shop/inventory-value/",
        );
        setData(response.data);
        setError(null);
      } catch (err) {
        setError("Failed to fetch inventory value. Please try again later.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const totalOrderValue = data.reduce(
    (acc, item) => acc + parseFloat(item.total_order_value),
    0,
  );
  const totalSaleValue = data.reduce(
    (acc, item) => acc + parseFloat(item.total_sale_value),
    0,
  );
  const potentialProfit = totalSaleValue - totalOrderValue;
  const currencyName = data[0]?.currency_name || "";

  return (
    <PageLayout logo={auth?.client?.logo} clientName={auth?.client?.name}>
      <div className="p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center mb-6">
            <ChartBarIcon className="w-8 h-8 text-gray-500 mr-3" />
            <h1 className="text-3xl font-bold text-gray-800">
              Inventory Valuation
            </h1>
          </div>

          {loading && (
            <div className="text-center py-10">
              <p className="text-gray-500">Loading inventory data...</p>
            </div>
          )}
          {error && (
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg"
              role="alert"
            >
              <strong className="font-bold">Error:</strong>
              <span className="block sm:inline ml-2">{error}</span>
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Totals Section */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div
                  className="text-white p-6 rounded-xl shadow-lg"
                  style={{ backgroundColor: "#069494ff" }}
                >
                  <div className="flex items-center">
                    <BanknotesIcon
                      className="w-8 h-8 mr-4"
                      style={{ color: "#ffce1bff" }}
                    />
                    <div>
                      <p
                        className="text-lg font-semibold"
                        style={{ color: "#ffce1bff" }}
                      >
                        Total Order Value
                      </p>
                      <p
                        className="text-3xl font-bold"
                        style={{ color: "#ffce1bff" }}
                      >
                        {`${currencyName} ${formatNumber(totalOrderValue)}`}
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  className="text-white p-6 rounded-xl shadow-lg"
                  style={{ backgroundColor: "#ffce1bff" }}
                >
                  <div className="flex items-center">
                    <BanknotesIcon
                      className="w-8 h-8 mr-4"
                      style={{ color: "#069494ff" }}
                    />
                    <div>
                      <p
                        className="text-lg font-semibold"
                        style={{ color: "#069494ff" }}
                      >
                        Total Sale Value
                      </p>
                      <p
                        className="text-3xl font-bold"
                        style={{ color: "#069494ff" }}
                      >
                        {`${currencyName} ${formatNumber(totalSaleValue)}`}
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  className="text-white p-6 rounded-xl shadow-lg"
                  style={{ backgroundColor: "#b7410eff" }}
                >
                  <div className="flex items-center">
                    <BanknotesIcon className="w-8 h-8 mr-4" />
                    <div>
                      <p className="text-lg font-semibold">Potential Profit</p>
                      <p className="text-3xl font-bold">{`${currencyName} ${formatNumber(potentialProfit)}`}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Branch Data - responsive layout */}
              {/* Cards on small screens, table on md+ */}
              <div className="space-y-4 md:space-y-0">
                {/* Mobile / small screens */}
                <div className="grid grid-cols-1 gap-4 md:hidden">
                  {data.map((item) => {
                    const order = parseFloat(item.total_order_value);
                    const sale = parseFloat(item.total_sale_value);
                    const profit = sale - order;
                    return (
                      <div
                        key={item.branch_id}
                        className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-3"
                      >
                        <div className="flex items-center">
                          <BuildingStorefrontIcon className="w-5 h-5 text-gray-400 mr-2" />
                          <span className="text-sm font-semibold text-gray-900">
                            {item.branch_name}
                          </span>
                        </div>
                        <div className="flex justify-between gap-4 text-sm text-gray-700">
                          <div className="flex-1">
                            <p className="text-xs uppercase text-gray-500 mb-1">
                              Order
                            </p>
                            <p className="font-medium">
                              {`${item.currency_symbol} ${formatNumber(order)}`}
                            </p>
                          </div>
                          <div className="flex-1 text-right">
                            <p className="text-xs uppercase text-gray-500 mb-1">
                              Sale
                            </p>
                            <p className="font-medium">
                              {`${item.currency_symbol} ${formatNumber(sale)}`}
                            </p>
                          </div>
                        </div>
                        <div className="mt-1 text-sm text-gray-700 flex justify-between">
                          <p className="text-xs uppercase text-gray-500">
                            Profit
                          </p>
                          <p className="font-medium">
                            {`${item.currency_symbol} ${formatNumber(profit)}`}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop / larger screens */}
                <div className="hidden md:block bg-white shadow-md rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Branch
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Order Value
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Sale Value
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Profit
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data.map((item) => {
                        const order = parseFloat(item.total_order_value);
                        const sale = parseFloat(item.total_sale_value);
                        const profit = sale - order;
                        return (
                          <tr key={item.branch_id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <BuildingStorefrontIcon className="w-5 h-5 text-gray-400 mr-3" />
                                <div className="text-sm font-medium text-gray-900">
                                  {item.branch_name}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700">
                              {`${item.currency_symbol} ${formatNumber(order)}`}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700">
                              {`${item.currency_symbol} ${formatNumber(sale)}`}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700">
                              {`${item.currency_symbol} ${formatNumber(profit)}`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
